import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebaseConfig";

import { formatTranslations, familyTranslations } from "./traductions.js";

// ======================================================
// CONFIGURACIÓN
// ======================================================

const CATEGORY_ORDER = [
  "Cervezas",
  "Sangrías",
  "Vinos",
  "Aperitivos",
  "Cocktails",
  "Gin",
  "Ron",
  "Vodka",
  "Whisky",
  "Anís",
  "Tequila",
  "Licor",
  "Refrescos",
];

const COLORS = {
  primary: "#93391C",
  primaryDark: "#722A16",
  cream: "#F8F4EF",
  paper: "#FFFDF9",
  text: "#28231F",
  muted: "#827970",
  border: "rgba(40,35,31,0.10)",
  selected: "rgba(147,57,28,0.08)",
};

const ALL_LABELS = {
  es: "Todo",
  cat: "Tot",
  en: "All",
  fr: "Tout",
};

const LOCALES = {
  es: "es-ES",
  cat: "ca-ES",
  en: "en-GB",
  fr: "fr-FR",
};

const DEFAULT_IMAGE = "/images/default-drinks.png";

// ======================================================
// HELPERS
// ======================================================

const getTranslatedFamily = (family, language) =>
  familyTranslations?.[language]?.[family] || family;

const getProductName = (product, language) =>
  product.translations?.[language]?.name ||
  product.producto ||
  product.nombre ||
  "Bebida";

const getProductDescription = (product, language) =>
  product.translations?.[language]?.description || "";

const getPrice = (price, language) => {
  const numericPrice = Number(price);

  if (!Number.isFinite(numericPrice)) {
    return null;
  }

  return new Intl.NumberFormat(LOCALES[language] || "es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(numericPrice);
};

const getOrder = (value) => {
  if (value === null || value === undefined || value === "") {
    return Number.MAX_SAFE_INTEGER;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER;
};

const sortProducts = (products) =>
  [...products].sort((a, b) => getOrder(a.ordenExcel) - getOrder(b.ordenExcel));

const getFormats = (product) =>
  Array.isArray(product.formatos) ? product.formatos : [];

// ======================================================
// COMPONENTE
// ======================================================

const BeverageProducts = ({ language = "es", idRestaurante }) => {
  const [beverages, setBeverages] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState("Cervezas");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // ----------------------------------------------------
  // CARGA FIRESTORE
  // ----------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    const fetchBeverages = async () => {
      if (!idRestaurante) {
        setBeverages([]);
        setLoading(false);
        setError(false);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        const collectionName = `bebidas-${idRestaurante}`;
        const snapshot = await getDocs(collection(db, collectionName));

        const data = snapshot.docs.map((document) => ({
          ...document.data(),
          _documentId: document.id,
        }));

        if (!cancelled) {
          setBeverages(data);
        }
      } catch (err) {
        console.error("Error al obtener bebidas:", err);

        if (!cancelled) {
          setError(true);
          setBeverages([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchBeverages();

    return () => {
      cancelled = true;
    };
  }, [idRestaurante]);

  // ----------------------------------------------------
  // FAMILIAS DISPONIBLES Y ORDENADAS
  // ----------------------------------------------------

  const availableFamilies = useMemo(() => {
    const existingFamilies = new Set(
      beverages.map((product) => product.familia).filter(Boolean),
    );

    const ordered = CATEGORY_ORDER.filter((family) =>
      existingFamilies.has(family),
    );

    const additional = [...existingFamilies]
      .filter((family) => !CATEGORY_ORDER.includes(family))
      .sort((a, b) => a.localeCompare(b, language));

    return ["Todo", ...ordered, ...additional];
  }, [beverages, language]);

  // Si la categoría actual no existe en el restaurante,
  // seleccionamos la primera categoría disponible.
  useEffect(() => {
    if (loading || availableFamilies.length === 0) return;

    if (!availableFamilies.includes(selectedFamily)) {
      setSelectedFamily(
        availableFamilies.includes("Cervezas")
          ? "Cervezas"
          : availableFamilies[0],
      );
    }
  }, [availableFamilies, loading, selectedFamily]);

  // ----------------------------------------------------
  // FILTRADO Y AGRUPACIÓN
  // ----------------------------------------------------

  const groupedProducts = useMemo(() => {
    const filtered =
      selectedFamily === "Todo"
        ? beverages
        : beverages.filter((product) => product.familia === selectedFamily);

    const groups = {};

    filtered.forEach((product) => {
      const groupName =
        selectedFamily === "Vinos"
          ? product.subfam || "Sin Clasificar"
          : product.familia || "Sin Clasificar";

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push(product);
    });

    Object.keys(groups).forEach((group) => {
      groups[group] = sortProducts(groups[group]);
    });

    return Object.entries(groups).sort(([a], [b]) => {
      if (selectedFamily === "Vinos") {
        return a.localeCompare(b, language);
      }

      const indexA = CATEGORY_ORDER.indexOf(a);
      const indexB = CATEGORY_ORDER.indexOf(b);

      if (indexA === -1 && indexB === -1) {
        return a.localeCompare(b, language);
      }

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }, [beverages, selectedFamily, language]);

  // ----------------------------------------------------
  // RENDER
  // ----------------------------------------------------

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 180,
        backgroundColor: COLORS.paper,
        color: COLORS.text,
        pb: 4,
      }}
    >
      {/* NAVEGACIÓN DE CATEGORÍAS */}

      {!loading && !error && beverages.length > 0 && (
        <Box
          sx={{
            position: "sticky",
            top: 47,
            zIndex: 20,
            backgroundColor: "rgba(255,253,249,0.96)",
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${COLORS.border}`,
            boxShadow: "0 3px 12px rgba(40,35,31,0.045)",
          }}
        >
          <Tabs
            value={selectedFamily}
            onChange={(_, value) => setSelectedFamily(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Categorías de bebidas"
            sx={{
              minHeight: 48,

              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                backgroundColor: COLORS.primary,
              },

              "& .MuiTabs-scrollButtons": {
                color: COLORS.primary,
                width: 28,
              },

              "& .MuiTab-root": {
                minHeight: 48,
                minWidth: "auto",
                px: { xs: 1.7, sm: 2.2 },
                color: COLORS.muted,
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 750,
                letterSpacing: "0.045em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                transition: "color 180ms ease",

                "&:hover": {
                  color: COLORS.primary,
                },

                "&.Mui-selected": {
                  color: COLORS.primaryDark,
                  fontWeight: 900,
                },
              },
            }}
          >
            {availableFamilies.map((family) => (
              <Tab
                key={family}
                value={family}
                label={
                  family === "Todo"
                    ? ALL_LABELS[language] || ALL_LABELS.es
                    : getTranslatedFamily(family, language)
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      <Container
        maxWidth="md"
        sx={{
          px: { xs: 1.7, sm: 3 },
          pt: { xs: 1.5, sm: 2.5 },
        }}
      >
        {/* CARGANDO */}

        {loading && (
          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress
              size={27}
              thickness={3}
              sx={{ color: COLORS.primary }}
            />

            <Typography
              sx={{
                color: COLORS.muted,
                fontSize: 11,
                letterSpacing: "0.04em",
              }}
            >
              {language === "cat"
                ? "Carregant begudes..."
                : language === "en"
                  ? "Loading drinks..."
                  : language === "fr"
                    ? "Chargement des boissons..."
                    : "Cargando bebidas..."}
            </Typography>
          </Box>
        )}

        {/* ERROR */}

        {!loading && error && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              borderRadius: 3,
              fontSize: 12,
            }}
          >
            {language === "cat"
              ? "No s'han pogut carregar les begudes."
              : language === "en"
                ? "Drinks could not be loaded."
                : language === "fr"
                  ? "Impossible de charger les boissons."
                  : "No se han podido cargar las bebidas."}
          </Alert>
        )}

        {/* SIN PRODUCTOS */}

        {!loading && !error && beverages.length === 0 && (
          <Box
            sx={{
              py: 7,
              px: 2,
              textAlign: "center",
            }}
          >
            <LocalBarOutlinedIcon
              sx={{
                fontSize: 36,
                color: "#B8A99B",
                mb: 1,
              }}
            />

            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 800,
                color: COLORS.text,
              }}
            >
              {language === "cat"
                ? "Encara no hi ha begudes"
                : language === "en"
                  ? "No drinks available yet"
                  : language === "fr"
                    ? "Aucune boisson disponible"
                    : "Todavía no hay bebidas"}
            </Typography>
          </Box>
        )}

        {/* LISTADO DE BEBIDAS */}

        {!loading &&
          !error &&
          groupedProducts.map(([group, products]) => (
            <Box key={group} sx={{ mb: 3.5 }}>
              {/* CABECERA DE FAMILIA */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.5,
                  mb: 0.8,
                }}
              >
                <Box
                  sx={{
                    width: 3,
                    height: 22,
                    borderRadius: 2,
                    backgroundColor: COLORS.primary,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  component="h2"
                  sx={{
                    fontSize: { xs: 13, sm: 15 },
                    fontWeight: 900,
                    letterSpacing: "0.065em",
                    textTransform: "uppercase",
                    color: COLORS.text,
                  }}
                >
                  {getTranslatedFamily(group, language)}
                </Typography>

                <Box sx={{ flex: 1 }} />

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                >
                  {products.length}
                </Typography>
              </Box>

              <Divider
                sx={{
                  mb: 0.5,
                  borderColor: COLORS.border,
                }}
              />

              {/* FILAS DE PRODUCTOS */}

              {products.map((product, index) => {
                const formats = getFormats(product);
                const productName = getProductName(product, language);
                const description = getProductDescription(product, language);

                return (
                  <React.Fragment
                    key={
                      product.codigo ||
                      product._documentId ||
                      `${productName}-${index}`
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: { xs: 1.2, sm: 1.8 },
                        py: { xs: 1.45, sm: 1.8 },
                        px: { xs: 0.3, sm: 0.8 },
                        borderRadius: 2,
                        transition: "background-color 180ms ease",

                        "&:hover": {
                          backgroundColor: "rgba(147,57,28,0.025)",
                        },
                      }}
                    >
                      {/* IMAGEN */}

                      <Box
                        sx={{
                          width: { xs: 62, sm: 76 },
                          height: { xs: 62, sm: 76 },
                          flexShrink: 0,
                          borderRadius: { xs: 2, sm: 2.5 },
                          overflow: "hidden",
                          backgroundColor: COLORS.cream,
                          border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        <Box
                          component="img"
                          src={product.imagenUrl || DEFAULT_IMAGE}
                          alt={productName}
                          loading="lazy"
                          onError={(event) => {
                            if (
                              event.currentTarget.src !==
                              window.location.origin + DEFAULT_IMAGE
                            ) {
                              event.currentTarget.src = DEFAULT_IMAGE;
                            }
                          }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            objectFit: "cover",
                            transition: "transform 250ms ease",

                            "&:hover": {
                              transform: "scale(1.045)",
                            },
                          }}
                        />
                      </Box>

                      {/* INFORMACIÓN */}

                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          pt: 0.15,
                        }}
                      >
                        <Typography
                          component="h3"
                          sx={{
                            color: COLORS.text,
                            fontSize: { xs: 12, sm: 14 },
                            fontWeight: 850,
                            lineHeight: 1.35,
                            letterSpacing: "-0.015em",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {productName}
                        </Typography>

                        {description && (
                          <Typography
                            sx={{
                              mt: 0.35,
                              color: COLORS.muted,
                              fontSize: { xs: 10, sm: 11 },
                              lineHeight: 1.5,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {description}
                          </Typography>
                        )}

                        {/* FORMATOS Y PRECIOS */}

                        {formats.length > 0 && (
                          <Stack
                            direction="row"
                            useFlexGap
                            flexWrap="wrap"
                            spacing={0.7}
                            sx={{
                              mt: 0.9,
                              rowGap: 0.65,
                            }}
                          >
                            {formats.map((format, formatIndex) => {
                              const translatedFormat =
                                formatTranslations?.[language]?.[
                                  format.formato
                                ] || format.formato;

                              const price = getPrice(format.precio, language);

                              return (
                                <Box
                                  key={`${format.formato}-${formatIndex}`}
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.7,
                                    flexWrap: "wrap",
                                    minHeight: 25,
                                    px: 0.9,
                                    py: 0.45,
                                    borderRadius: 1.5,
                                    backgroundColor: COLORS.cream,
                                    border: `1px solid ${COLORS.border}`,
                                    maxWidth: "100%",
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: COLORS.muted,
                                      fontSize: { xs: 9, sm: 10 },
                                      fontWeight: 650,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {translatedFormat}

                                    {format.cantidad !== null &&
                                      format.cantidad !== undefined &&
                                      format.cantidad !== "" && (
                                        <>
                                          {" "}
                                          <Box
                                            component="span"
                                            sx={{
                                              color: "#A49A90",
                                              fontStyle: "italic",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {format.cantidad}
                                          </Box>
                                        </>
                                      )}
                                  </Typography>

                                  {price !== null && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: COLORS.primaryDark,
                                        fontSize: { xs: 10, sm: 11 },
                                        fontWeight: 900,
                                        whiteSpace: "nowrap",
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {price}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>

                      <KeyboardArrowRightRoundedIcon
                        sx={{
                          mt: 0.25,
                          color: "rgba(130,121,112,0.4)",
                          fontSize: 19,
                          flexShrink: 0,
                        }}
                      />
                    </Box>

                    {index < products.length - 1 && (
                      <Divider
                        sx={{
                          borderColor: "rgba(40,35,31,0.055)",
                          ml: { xs: 0, sm: 1 },
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          ))}

        {/* FILTRO SIN RESULTADOS */}

        {!loading &&
          !error &&
          beverages.length > 0 &&
          groupedProducts.length === 0 && (
            <Typography
              sx={{
                py: 5,
                textAlign: "center",
                color: COLORS.muted,
                fontSize: 12,
              }}
            >
              {language === "cat"
                ? "No hi ha begudes en aquesta categoria."
                : language === "en"
                  ? "No drinks in this category."
                  : language === "fr"
                    ? "Aucune boisson dans cette catégorie."
                    : "No hay bebidas en esta categoría."}
            </Typography>
          )}
      </Container>
    </Box>
  );
};

export default BeverageProducts;
